/**
 * VerificationRequestsPage - Admin Verification Management
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Admin table, modals, action buttons, status badges, filters.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, CheckCircle, Clock, Loader2 } from "lucide-react";

// Hooks & Contexts
import { useTranslation, useSnackbar } from "@/shared/hooks";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button } from "@/shared/ui/components";
import { TextArea } from "@/shared/ui/components";

// API
import {
  listVerificationRequests,
  getVerificationRequestDetail,
  approveVerificationRequest,
  rejectVerificationRequest,
  type VerificationRequest,
  type VerificationRequestSummary,
} from "@/shared/api/verificationApi";

// Constants
import { ROUTES } from "@/shared/constants";

// Helper function to check if user has dev access
function hasDevAccess(globalRoles: string[] | undefined): boolean {
  return (
    globalRoles?.some((role) => role === "admin" || role === "developer") ??
    false
  );
}

// Helper to format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const classes =
    {
      pending: "bg-yellow-100 text-yellow-800",
      verified: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      unverified: "bg-gray-100 text-gray-800",
    }[status] || "bg-gray-100 text-gray-800";

  return (
    <Text
      as="span"
      variant="caption"
      className={`px-2 py-1 font-medium rounded-full ${classes}`}
    >
      {status}
    </Text>
  );
}

// Details Modal Component
interface DetailsModalProps {
  request: VerificationRequest;
  onClose: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
}

function DetailsModal({
  request,
  onClose,
  onApprove,
  onReject,
}: DetailsModalProps) {
  const t = useTranslation();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await onApprove();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setSubmitting(true);
    try {
      await onReject(rejectionReason);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <Card
          variant="default"
          padding="none"
          className="shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <Box className="sticky top-0 bg-white border-b border-gray-200 p-6">
            <Text variant="h3" className="text-gray-900">
              {t("verificationViewDetails")}
            </Text>
          </Box>

          {/* Content */}
          <Box className="p-6 space-y-6">
            {/* Status */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <StatusBadge status={request.status} />
            </Box>

            {/* Account Type */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("verificationAccountType")}
              </label>
              <Text variant="body" className="text-gray-900">
                {request.account_type === "corporate"
                  ? t("verificationAccountTypeCorporate")
                  : t("verificationAccountTypeIndividual")}
              </Text>
            </Box>

            {/* Company Name */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("verificationCompanyName")}
              </label>
              <Text variant="body" className="text-gray-900">
                {request.company_name}
              </Text>
            </Box>

            {/* License Number */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("verificationLicenseNumber")}
              </label>
              <Text variant="body" className="text-gray-900 font-mono">
                {request.license_number}
              </Text>
            </Box>

            {/* Corporate Email */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("verificationCorporateEmail")}
              </label>
              <Text variant="body" className="text-gray-900">
                {request.corporate_email}
              </Text>
            </Box>

            {/* Official Website */}
            {request.official_website && (
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("verificationOfficialWebsite")}
                </label>
                <a
                  href={request.official_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {request.official_website}
                </a>
              </Box>
            )}

            {/* Documents */}
            <Box>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documents
              </label>
              {request.documents?.agency_license_url ||
              request.documents?.officer_id_url ? (
                <Box className="space-y-2">
                  {request.documents.agency_license_url && (
                    <a
                      href={request.documents.agency_license_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-blue-600 hover:underline"
                    >
                      {t("verificationAgencyLicense")}
                    </a>
                  )}
                  {request.documents.officer_id_url && (
                    <a
                      href={request.documents.officer_id_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-blue-600 hover:underline"
                    >
                      {t("verificationOfficerId")}
                    </a>
                  )}
                </Box>
              ) : (
                <Text variant="body" className="text-gray-500 text-sm">
                  {t("verificationUploadComingSoon")}
                </Text>
              )}
            </Box>

            {/* Dates */}
            <Box className="grid grid-cols-2 gap-4">
              <Box>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submitted
                </label>
                <Text variant="body" className="text-gray-900 text-sm">
                  {formatDate(request.submitted_at)}
                </Text>
              </Box>
              {request.reviewed_at && (
                <Box>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reviewed
                  </label>
                  <Text variant="body" className="text-gray-900 text-sm">
                    {formatDate(request.reviewed_at)}
                  </Text>
                </Box>
              )}
            </Box>

            {/* Rejection Reason */}
            {request.rejection_reason && (
              <Card
                variant="default"
                padding="md"
                className="bg-red-50 border-red-200"
              >
                <label className="block text-sm font-medium text-red-800 mb-1">
                  {t("verificationRejectionReason")}
                </label>
                <Text variant="body" className="text-red-700">
                  {request.rejection_reason}
                </Text>
              </Card>
            )}
          </Box>

          {/* Actions */}
          <Box className="border-t border-gray-200 p-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              {t("common.close")}
            </Button>
            {request.status === "pending" && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => setShowRejectModal(true)}
                  disabled={submitting}
                >
                  {t("verificationReject")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  disabled={submitting}
                  loading={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t("verificationApprove")}
                </Button>
              </>
            )}
          </Box>
        </Card>
      </Box>

      {/* Rejection Modal */}
      {showRejectModal && (
        <Box className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <Card
            variant="default"
            padding="md"
            className="shadow-2xl max-w-md w-full"
          >
            <Text variant="h4" className="mb-4">
              {t("verificationRejectionReason")}
            </Text>
            <TextArea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t("verificationRejectionReasonPlaceholder")}
              rows={4}
            />
            <Box className="flex justify-end gap-3 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowRejectModal(false)}
              >
                {t("back")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || submitting}
                loading={submitting}
              >
                {t("verificationReject")}
              </Button>
            </Box>
          </Card>
        </Box>
      )}
    </>
  );
}

export const VerificationRequestsPage = observer(
  function VerificationRequestsPage() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { authStore } = useStore();
    const { getIdToken, authMe } = authStore;

    // State
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState<VerificationRequestSummary[]>([]);
    const [selectedRequest, setSelectedRequest] =
      useState<VerificationRequest | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [filter, setFilter] = useState<"all" | "pending">("pending");

    // Check access
    const hasAccess = useMemo(
      () => hasDevAccess(authMe?.global_roles),
      [authMe?.global_roles],
    );

    // Filtered requests
    const filteredRequests = useMemo(() => {
      if (filter === "all") return requests;
      return requests.filter((req) => req.status === "pending");
    }, [requests, filter]);

    // Fetch requests
    useEffect(() => {
      if (!hasAccess) return;

      const fetchRequests = async () => {
        setLoading(true);
        try {
          const token = await getIdToken();
          if (!token) return;

          const data = await listVerificationRequests(token);
          setRequests(data.requests);
        } catch (error) {
          console.error("Failed to fetch verification requests:", error);
          showSnackbar("Failed to load verification requests", "error");
        } finally {
          setLoading(false);
        }
      };

      fetchRequests();
    }, [hasAccess, getIdToken, showSnackbar]);

    // View request details
    const handleViewRequest = useCallback(
      async (requestId: string) => {
        setLoadingDetail(true);
        try {
          const token = await getIdToken();
          if (!token) return;

          const detail = await getVerificationRequestDetail(token, requestId);
          setSelectedRequest(detail);
        } catch (error) {
          console.error("Failed to fetch request details:", error);
          showSnackbar("Failed to load request details", "error");
        } finally {
          setLoadingDetail(false);
        }
      },
      [getIdToken, showSnackbar],
    );

    // Handle approve
    const handleApprove = useCallback(
      async (requestId: string) => {
        try {
          const token = await getIdToken();
          if (!token) return;

          await approveVerificationRequest(token, requestId);
          showSnackbar("Verification approved successfully", "success");

          // Refresh list
          const data = await listVerificationRequests(token);
          setRequests(data.requests);
        } catch (error) {
          console.error("Failed to approve verification:", error);
          showSnackbar("Failed to approve verification", "error");
        }
      },
      [getIdToken, showSnackbar],
    );

    // Handle reject
    const handleReject = useCallback(
      async (requestId: string, reason: string) => {
        try {
          const token = await getIdToken();
          if (!token) return;

          await rejectVerificationRequest(token, requestId, reason);
          showSnackbar("Verification rejected", "info");

          // Refresh list
          const data = await listVerificationRequests(token);
          setRequests(data.requests);
        } catch (error) {
          console.error("Failed to reject verification:", error);
          showSnackbar("Failed to reject verification", "error");
        }
      },
      [getIdToken, showSnackbar],
    );

    // Redirect if not authorized
    if (!hasAccess) {
      return (
        <BasePage
          title={t("verificationRequestsTitle")}
          description="Partner verification requests"
          maxWidth="xl"
          background="gray"
        >
          <Card variant="default" padding="lg" className="text-center">
            <Text variant="body" color="secondary">
              {t("notAuthorized")}
            </Text>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.PROFILE)}
              className="mt-4"
            >
              {t("backToProfile")}
            </Button>
          </Card>
        </BasePage>
      );
    }

    return (
      <BasePage
        title={t("verificationRequestsTitle")}
        description="Review and approve partner verification requests"
        maxWidth="xl"
        background="gray"
      >
        {/* Header */}
        <Box className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.PROFILE)}
            className="-ml-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Box>
            <Text variant="h2" className="text-gray-900">
              {t("verificationRequestsTitle")}
            </Text>
            <Text variant="body" color="secondary">
              Review and approve partner verification requests
            </Text>
          </Box>
        </Box>

        {/* Filter Tabs */}
        <Box className="flex gap-2 mb-6">
          <Button
            variant={filter === "pending" ? "primary" : "secondary"}
            onClick={() => setFilter("pending")}
          >
            <Clock className="w-4 h-4 mr-2" />
            Pending ({requests.filter((r) => r.status === "pending").length})
          </Button>
          <Button
            variant={filter === "all" ? "primary" : "secondary"}
            onClick={() => setFilter("all")}
          >
            All ({requests.length})
          </Button>
        </Box>

        {/* Requests Table */}
        <Card
          variant="default"
          padding="none"
          className="shadow overflow-hidden"
        >
          {loading ? (
            <Box className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
            </Box>
          ) : filteredRequests.length === 0 ? (
            <Box className="text-center py-12">
              <Text variant="body" color="secondary">
                No verification requests found
              </Text>
            </Box>
          ) : (
            <Box as="table" className="w-full">
              <Box as="thead" className="bg-gray-50 border-b border-gray-200">
                <Box as="tr">
                  <Box
                    as="th"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Company
                  </Box>
                  <Box
                    as="th"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Type
                  </Box>
                  <Box
                    as="th"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Submitted
                  </Box>
                  <Box
                    as="th"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </Box>
                  <Box
                    as="th"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </Box>
                </Box>
              </Box>
              <Box as="tbody" className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <Box
                    as="tr"
                    key={request.request_id}
                    className="hover:bg-gray-50"
                  >
                    <Box as="td" className="px-6 py-4 whitespace-nowrap">
                      <Text
                        variant="body"
                        className="text-sm font-medium text-gray-900"
                      >
                        {request.org_name}
                      </Text>
                      <Text variant="body" className="text-sm text-gray-500">
                        {request.requested_by_email}
                      </Text>
                    </Box>
                    <Box as="td" className="px-6 py-4 whitespace-nowrap">
                      <Text variant="body" className="text-sm text-gray-900">
                        {request.account_type === "corporate"
                          ? "Corporate"
                          : "Individual"}
                      </Text>
                    </Box>
                    <Box as="td" className="px-6 py-4 whitespace-nowrap">
                      <Text variant="body" className="text-sm text-gray-900">
                        {formatDate(request.submitted_at)}
                      </Text>
                    </Box>
                    <Box as="td" className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={request.status} />
                    </Box>
                    <Box
                      as="td"
                      className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                    >
                      <Box className="flex justify-end gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleViewRequest(request.request_id)}
                          disabled={loadingDetail}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {request.status === "pending" && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApprove(request.request_id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Card>

        {/* Details Modal */}
        {selectedRequest && (
          <DetailsModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
            onApprove={() => handleApprove(selectedRequest.request_id)}
            onReject={(reason) =>
              handleReject(selectedRequest.request_id, reason)
            }
          />
        )}
      </BasePage>
    );
  },
);
